/**
 * MessageBubble — single chat message render with alignment, styling, and
 * layout based on sender + message type.
 *
 * Memoized: a conversation can render hundreds of messages, and any new
 * message at the bottom (or a read-state flip in the FlashList recycler)
 * would otherwise re-render the whole list. FlashList v2 makes
 * memoization especially important — props passed to the list must be
 * stable so the recycler can skip child re-renders. Props here are
 * primitives + a stable `MessageItem` reference from the query cache,
 * so the default shallow compare is correct. **Do not pass `colors` or
 * any object built fresh in the parent's render — it would defeat the
 * memo.**
 *
 * `useTheme()` is read inside the component so the consuming screen does
 * not need to thread `isDark` through props. The theme store is stable so
 * this does not retrigger renders unless the theme actually flips.
 */

import { GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import { formatBookingDateTime } from '@/lib/utils/dates'
import { memo, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Linking, Text, View } from 'react-native'
import { AlertCircle, Check, CheckCheck, Clock3, MapPin, Navigation } from '@/components/ui/icons'
import { Image } from '../ui/Image'
import { Pressable } from '../ui/Pressable'
import { ReplyContext } from './ReplyContext'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '../../lib/theme/tokens'
import { COLORS, FONTS, RADIUS } from '../../lib/brand'
import type { MessageItem } from '../../lib/types/messaging.types'

const CHAT_BUBBLE_GEOMETRY = {
  maxWidth: '78%',
  horizontalInset: SPACING.lg,
  groupedGap: 2,
  standaloneTopGap: SPACING.xs,
  standaloneBottomGap: SPACING.sm,
  bodyFontSize: 15,
  // Sin `lineHeight` en el texto de la burbuja, a propósito: con `lineHeight`
  // el layout nativo de Android quedaba desincronizado del layout de Yoga y
  // el pintado cortaba la última palabra del mensaje ("Voy en camino" se veía
  // como "Voy en", "Ya llegué" como "Ya"). Medido en el Redmi (HyperOS,
  // Android 16): sin el span de lineHeight el texto sale completo siempre. El
  // leading natural del font es ~18px, así que el padding vertical sube de 8
  // a 10 para conservar la altura de burbuja que había con lineHeight 21.
  bodyVerticalPadding: 10,
  // 12 is the floor (CLAUDE.md / WCAG). Chat timestamps used to render at 11.
  metaFontSize: 12,
  metaGap: 4,
  metaTopGap: 3,
  metaIconSize: 10,
  dayPillPaddingX: SPACING.sm,
  dayPillPaddingY: 5,
  systemFontSize: 13,
  systemTimeFontSize: 12,
  imageWidth: 220,
  imageHeight: 180,
} as const

interface MessageBubbleProps {
  message: MessageItem
  isMine: boolean
  isGroupedWithPrevious?: boolean
  isGroupedWithNext?: boolean
  isPending?: boolean
  dayLabel?: string | null
  /**
   * Stable across renders BY CONTRACT: FlashList memoizes rows on shallow
   * compare, and a callback rebuilt per row would re-render the whole recycle
   * pool on every keystroke. The screen passes one `useCallback` and the bubble
   * calls it with its own message.
   */
  onPressImage?: (message: MessageItem) => void
  /**
   * Same identity contract as `onPressImage`. Only fired for own messages whose
   * send failed; the bubble shows the failed state and the tap re-sends.
   */
  onRetryMessage?: (message: MessageItem) => void
  /** Long-press reply affordance; unavailable for optimistic/failed rows. */
  onReplyMessage?: (message: MessageItem) => void
  /** Resolved by the screen from the same participant source as the header. */
  replyAuthorLabel?: string
  /**
   * Own message only: the counterpart's read cursor passed this message's
   * timestamp. Absent/false renders the single "sent" tick; a thread with no
   * receipt yet (or an older client) simply keeps showing "sent", never a
   * wrong "read".
   */
  isRead?: boolean
}

function MessageBubbleBase({
  message,
  isMine,
  isGroupedWithPrevious = false,
  isGroupedWithNext = false,
  isPending = false,
  dayLabel,
  onPressImage,
  onRetryMessage,
  onReplyMessage,
  replyAuthorLabel,
  isRead = false,
}: MessageBubbleProps) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  // Same timezone contract as the day separators above the bubbles
  // (America/El_Salvador): a bubble must never read like it belongs to a
  // different day than the separator it sits under.
  const timestamp = useMemo(
    () =>
      formatBookingDateTime(message.createdAt, i18n.language, {
        day: undefined,
        month: undefined,
        year: undefined,
      }),
    [message.createdAt, i18n.language],
  )

  /**
   * A failed own message stays in the thread marked, and the whole bubble is
   * the retry target — a 12pt "retry" link beside the timestamp would be the
   * smallest thing on the row. `isPending` and `canRetry` are mutually
   * exclusive: a failed row is no longer in flight.
   */
  const canRetry = isMine && message.sendFailed === true && Boolean(onRetryMessage)
  const canReply =
    message.messageType !== 'system' && !message.sendFailed && !isPending && Boolean(onReplyMessage)
  const replyContext = message.replyToId ? (
    <ReplyContext
      reply={message.replyTo}
      authorLabel={replyAuthorLabel ?? t('messages.reply_message')}
      tone={isMine ? 'sent' : 'received'}
      testID="chat.reply-context"
    />
  ) : null
  // React Native fires `onPress` after `onLongPress`. Image and location
  // bubbles already own a tap action, so consume that trailing press or a
  // deliberate reply would also open the photo/maps app.
  const consumedLongPressRef = useRef(false)
  const handleLongPress = canReply
    ? () => {
        consumedLongPressRef.current = true
        onReplyMessage?.(message)
      }
    : undefined
  const handleTap = (action: () => void) => {
    if (consumedLongPressRef.current) {
      consumedLongPressRef.current = false
      return
    }
    action()
  }

  const meta = !isGroupedWithNext ? (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: CHAT_BUBBLE_GEOMETRY.metaGap,
        marginTop: CHAT_BUBBLE_GEOMETRY.metaTopGap,
        alignSelf: isMine ? 'flex-end' : 'flex-start',
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: CHAT_BUBBLE_GEOMETRY.metaFontSize,
          color: colors.textSecondary,
        }}
      >
        {timestamp}
      </Text>
      {canRetry ? (
        <View
          accessibilityLabel={t('messages.send_failed_tap')}
          style={{ flexDirection: 'row', alignItems: 'center', gap: CHAT_BUBBLE_GEOMETRY.metaGap }}
        >
          <AlertCircle
            size={CHAT_BUBBLE_GEOMETRY.metaIconSize}
            color={colors.error}
            strokeWidth={2}
          />
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: CHAT_BUBBLE_GEOMETRY.metaFontSize,
              color: colors.error,
            }}
          >
            {t('messages.send_failed_tap')}
          </Text>
        </View>
      ) : isPending ? (
        <View
          accessibilityLabel={t('messages.sending')}
          style={{ flexDirection: 'row', alignItems: 'center', gap: CHAT_BUBBLE_GEOMETRY.metaGap }}
        >
          <Clock3
            size={CHAT_BUBBLE_GEOMETRY.metaIconSize}
            color={colors.textSecondary}
            strokeWidth={2}
          />
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: CHAT_BUBBLE_GEOMETRY.metaFontSize,
              color: colors.textSecondary,
            }}
          >
            {t('messages.sending')}
          </Text>
        </View>
      ) : isMine ? (
        // Sent vs read. Grey single tick = the row is stored; brand-purple
        // double tick = the counterpart's cursor passed it. Deliberately NOT
        // the error red: in light mode `textAccent` and `error` are the same
        // hex, and a failed send and a read receipt must never look alike.
        <View
          accessibilityLabel={isRead ? t('messages.read') : t('messages.sent')}
          style={{ flexDirection: 'row', alignItems: 'center' }}
        >
          {isRead ? (
            <CheckCheck
              size={CHAT_BUBBLE_GEOMETRY.metaIconSize + 3}
              color={COLORS.purple.DEFAULT}
              strokeWidth={2.4}
            />
          ) : (
            <Check
              size={CHAT_BUBBLE_GEOMETRY.metaIconSize + 3}
              color={colors.textSecondary}
              strokeWidth={2.4}
            />
          )}
        </View>
      ) : null}
    </View>
  ) : null

  const daySeparator = dayLabel ? (
    <View
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: SPACING.sm,
        paddingBottom: SPACING.xs,
      }}
    >
      <View
        style={{
          paddingHorizontal: CHAT_BUBBLE_GEOMETRY.dayPillPaddingX,
          paddingVertical: CHAT_BUBBLE_GEOMETRY.dayPillPaddingY,
          borderRadius: RADIUS.full,
          backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(26,21,37,0.06)',
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: CHAT_BUBBLE_GEOMETRY.metaFontSize,
            color: colors.textSecondary,
          }}
        >
          {dayLabel}
        </Text>
      </View>
    </View>
  ) : null

  // ── System message ─────────────────────────────────────────────────────
  if (message.messageType === 'system') {
    return (
      <>
        {daySeparator}
        <View
          style={{
            alignItems: 'center',
            paddingVertical: SPACING.sm,
            paddingHorizontal: SPACING.md,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: CHAT_BUBBLE_GEOMETRY.systemFontSize,
              color: colors.textMuted,
              textAlign: 'center',
            }}
          >
            {message.content}
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: CHAT_BUBBLE_GEOMETRY.systemTimeFontSize,
              color: colors.textSecondary,
              marginTop: SPACING.xs,
            }}
          >
            {timestamp}
          </Text>
        </View>
      </>
    )
  }

  // ── Image message ──────────────────────────────────────────────────────
  if (message.messageType === 'image' && message.mediaUrl) {
    return (
      <>
        {daySeparator}
        <View
          style={{
            alignSelf: isMine ? 'flex-end' : 'flex-start',
            maxWidth: CHAT_BUBBLE_GEOMETRY.maxWidth,
            marginHorizontal: CHAT_BUBBLE_GEOMETRY.horizontalInset,
            marginTop: isGroupedWithPrevious
              ? CHAT_BUBBLE_GEOMETRY.groupedGap
              : CHAT_BUBBLE_GEOMETRY.standaloneTopGap,
            marginBottom: isGroupedWithNext
              ? CHAT_BUBBLE_GEOMETRY.groupedGap
              : CHAT_BUBBLE_GEOMETRY.standaloneBottomGap,
          }}
        >
          {replyContext ? <View style={{ marginBottom: SPACING.xs }}>{replyContext}</View> : null}
          <View
            style={{
              borderRadius: RADIUS.lg,
              overflow: 'hidden',
              borderWidth: isMine ? 0 : 1,
              borderColor: colors.border,
              backgroundColor: colors.surface,
            }}
          >
            {canRetry ? (
              <Pressable
                onPress={() => onRetryMessage?.(message)}
                accessibilityRole="button"
                accessibilityLabel={t('messages.retry_send')}
                testID="chat.retry-send"
              >
                <Image
                  source={message.mediaUrl}
                  recyclingKey={message.id}
                  blurhash={message.mediaBlurhash ?? GENERIC_IMAGE_BLURHASH}
                  width={CHAT_BUBBLE_GEOMETRY.imageWidth}
                  height={CHAT_BUBBLE_GEOMETRY.imageHeight}
                  borderRadius={RADIUS.lg}
                  contentFit="cover"
                  accessibilityLabel={t('messages.image')}
                />
              </Pressable>
            ) : onPressImage ? (
              <Pressable
                onPress={() => handleTap(() => onPressImage(message))}
                onLongPress={handleLongPress}
                delayLongPress={350}
                accessibilityRole="button"
                accessibilityLabel={t('messages.open_image')}
                testID="chat.image-open"
              >
                <Image
                  source={message.mediaUrl}
                  recyclingKey={message.id}
                  blurhash={message.mediaBlurhash ?? GENERIC_IMAGE_BLURHASH}
                  width={CHAT_BUBBLE_GEOMETRY.imageWidth}
                  height={CHAT_BUBBLE_GEOMETRY.imageHeight}
                  borderRadius={RADIUS.lg}
                  contentFit="cover"
                  accessibilityLabel={t('messages.image')}
                />
              </Pressable>
            ) : (
              <Pressable onLongPress={handleLongPress} delayLongPress={350}>
                <Image
                  source={message.mediaUrl}
                  recyclingKey={message.id}
                  blurhash={message.mediaBlurhash ?? GENERIC_IMAGE_BLURHASH}
                  width={CHAT_BUBBLE_GEOMETRY.imageWidth}
                  height={CHAT_BUBBLE_GEOMETRY.imageHeight}
                  borderRadius={RADIUS.lg}
                  contentFit="cover"
                  accessibilityLabel={t('messages.image')}
                />
              </Pressable>
            )}
          </View>
          {meta}
        </View>
      </>
    )
  }

  // ── Location message ───────────────────────────────────────────────────
  //
  // The single most common reason a rental pair left for WhatsApp was
  // "mandame la ubicación". A pinned point inside the thread kills that
  // reason: no phone number, no external app required, and the tap hands off
  // to whichever maps app the RECEIVER already uses.
  if (
    message.messageType === 'location' &&
    message.locationLat != null &&
    message.locationLng != null
  ) {
    const lat = message.locationLat
    const lng = message.locationLng

    return (
      <>
        {daySeparator}
        <View
          style={{
            alignSelf: isMine ? 'flex-end' : 'flex-start',
            maxWidth: CHAT_BUBBLE_GEOMETRY.maxWidth,
            marginHorizontal: CHAT_BUBBLE_GEOMETRY.horizontalInset,
            marginTop: isGroupedWithPrevious
              ? CHAT_BUBBLE_GEOMETRY.groupedGap
              : CHAT_BUBBLE_GEOMETRY.standaloneTopGap,
            marginBottom: isGroupedWithNext
              ? CHAT_BUBBLE_GEOMETRY.groupedGap
              : CHAT_BUBBLE_GEOMETRY.standaloneBottomGap,
          }}
        >
          <Pressable
            onPress={
              canRetry
                ? () => onRetryMessage?.(message)
                : () =>
                    handleTap(
                      () => void Linking.openURL(`https://maps.google.com/?q=${lat},${lng}`),
                    )
            }
            onLongPress={handleLongPress}
            delayLongPress={350}
            accessibilityRole="button"
            accessibilityLabel={canRetry ? t('messages.retry_send') : t('messages.open_location')}
            testID={canRetry ? 'chat.retry-send' : 'chat.location-open'}
            style={{
              backgroundColor: isMine ? COLORS.coral.DEFAULT : colors.surface,
              borderRadius: RADIUS.lg,
              paddingHorizontal: SPACING.md,
              paddingVertical: SPACING.sm,
              borderWidth: isMine ? 0 : 1,
              borderColor: colors.border,
              borderTopRightRadius: isMine && isGroupedWithPrevious ? RADIUS.md : RADIUS.lg,
              borderBottomRightRadius: isMine && !isGroupedWithNext ? RADIUS.xs : RADIUS.lg,
              borderTopLeftRadius: !isMine && isGroupedWithPrevious ? RADIUS.md : RADIUS.lg,
              borderBottomLeftRadius: !isMine && !isGroupedWithNext ? RADIUS.xs : RADIUS.lg,
            }}
          >
            {replyContext ? <View style={{ marginBottom: SPACING.xs }}>{replyContext}</View> : null}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <MapPin size={20} color={isMine ? '#FFFFFF' : COLORS.coral.dark} strokeWidth={2.2} />
              <View style={{ minWidth: 0, flexShrink: 1 }}>
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: CHAT_BUBBLE_GEOMETRY.bodyFontSize,
                    color: isMine ? '#FFFFFF' : colors.textPrimary,
                  }}
                >
                  {t('messages.shared_location')}
                </Text>
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: CHAT_BUBBLE_GEOMETRY.metaFontSize,
                    color: isMine ? 'rgba(255,255,255,0.85)' : colors.textSecondary,
                  }}
                >
                  {lat.toFixed(5)}, {lng.toFixed(5)}
                </Text>
              </View>
              <Navigation
                size={16}
                color={isMine ? 'rgba(255,255,255,0.9)' : colors.textSecondary}
                strokeWidth={2.2}
              />
            </View>
          </Pressable>
          {meta}
        </View>
      </>
    )
  }

  // ── Text message (default) ─────────────────────────────────────────────
  return (
    <>
      {daySeparator}
      <View
        style={{
          alignSelf: isMine ? 'flex-end' : 'flex-start',
          maxWidth: CHAT_BUBBLE_GEOMETRY.maxWidth,
          marginHorizontal: CHAT_BUBBLE_GEOMETRY.horizontalInset,
          marginTop: isGroupedWithPrevious
            ? CHAT_BUBBLE_GEOMETRY.groupedGap
            : CHAT_BUBBLE_GEOMETRY.standaloneTopGap,
          marginBottom: isGroupedWithNext
            ? CHAT_BUBBLE_GEOMETRY.groupedGap
            : CHAT_BUBBLE_GEOMETRY.standaloneBottomGap,
        }}
      >
        <Pressable
          onPress={canRetry ? () => onRetryMessage?.(message) : undefined}
          onLongPress={handleLongPress}
          delayLongPress={350}
          accessibilityRole={canRetry ? 'button' : undefined}
          accessibilityLabel={canRetry ? t('messages.retry_send') : undefined}
          testID={canRetry ? 'chat.retry-send' : undefined}
          style={{
            backgroundColor: isMine ? COLORS.coral.DEFAULT : colors.surface,
            borderRadius: RADIUS.lg,
            paddingHorizontal: SPACING.md,
            paddingVertical: CHAT_BUBBLE_GEOMETRY.bodyVerticalPadding,
            borderWidth: isMine ? 0 : 1,
            borderColor: colors.border,
            borderTopRightRadius: isMine && isGroupedWithPrevious ? RADIUS.md : RADIUS.lg,
            borderBottomRightRadius: isMine && !isGroupedWithNext ? RADIUS.xs : RADIUS.lg,
            borderTopLeftRadius: !isMine && isGroupedWithPrevious ? RADIUS.md : RADIUS.lg,
            borderBottomLeftRadius: !isMine && !isGroupedWithNext ? RADIUS.xs : RADIUS.lg,
          }}
        >
          {replyContext ? <View style={{ marginBottom: SPACING.xs }}>{replyContext}</View> : null}
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: CHAT_BUBBLE_GEOMETRY.bodyFontSize,
              color: isMine ? '#FFFFFF' : colors.textPrimary,
            }}
          >
            {message.content}
          </Text>
        </Pressable>
        {meta}
      </View>
    </>
  )
}

export const MessageBubble = memo(MessageBubbleBase)
